import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import type { InputNode } from '../../types';
import { buildExtensions } from './extensions';
import { ToggleExpandButton } from './ToggleExpandButton';

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
  nodeDefaults?: { richtext?: { minHeight?: number; maxHeight?: number } };
  /** 展开/收起变化时上报给父级（key 为本节点 key）。父级据此进入「全屏态」：
   *  隐藏其他节点 + 编辑区加 rte-richtext-fullscreen-active（对齐 V1 覆盖式展开）。 */
  onExpandChange?: (key: string, expanded: boolean) => void;
};

/**
 * RichtextNode wraps a TipTap editor instance per node, providing rich-text editing
 * isolated from other RichtextNodes (multiple instances can coexist with no cross-talk).
 *
 * **Architecture invariants:**
 * - Each instance owns its own TipTap Editor (via useEditor); EditorState is not shared
 * - External `value` updates are merged only when the editor is NOT focused (preserves user input)
 * - `onChange` is captured via ref, so callers can pass fresh closures per render without
 *   stale-closure bugs (the editor's onUpdate always reads the latest onChange)
 *
 * **Future extensions** (toolbar, link, image, mention, list, heading) should land in this
 * directory only — they affect a single node, never the surrounding container or other
 * RichtextNode instances.
 */
export const RichtextNode: React.FC<Props> = React.memo(
  ({ node, value, onChange, disabled, error, nodeDefaults, onExpandChange }) => {
    const minHeight =
      typeof node.minHeight === 'number'
        ? node.minHeight
        : nodeDefaults?.richtext?.minHeight ?? 40;
    const rawMaxHeight =
      typeof node.maxHeight === 'number'
        ? node.maxHeight
        : nodeDefaults?.richtext?.maxHeight ?? 160;
    const maxHeight = Math.max(rawMaxHeight, minHeight);
    const expandable = node.expandable !== false;
    const [expanded, setExpanded] = useState(false);

    // I1 fix: stable refs for onUpdate closure. useEditor only runs once on mount,
    // so its onUpdate captures the initial onChange/key. Using refs lets callers
    // pass fresh closures across re-renders without stale invocations.
    const onChangeRef = useRef(onChange);
    const keyRef = useRef(node.key);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
      keyRef.current = node.key;
    }, [node.key]);

    const editor = useEditor({
      extensions: buildExtensions({ placeholder: node.placeholder }),
      content: value || '',
      editable: !disabled,
      onUpdate: ({ editor }) => {
        const k = keyRef.current;
        if (!k) return;
        onChangeRef.current(k, editor.getHTML());
      },
      editorProps: {
        attributes: {
          class: 'rte-richtext',
          'data-type': 'richtext',
          'data-key': node.key ?? '',
        },
      },
    });

    // 外部 value 同步（不打断输入：仅在 editor 非聚焦时合并）
    useEffect(() => {
      if (!editor) return;
      const cur = editor.getHTML();
      const next = value ?? '';
      if (cur === next) return;
      if (editor.isFocused) return;
      editor.commands.setContent(next, false);
    }, [value, editor]);

    // disabled 状态同步
    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [disabled, editor]);

    const onToggle = useCallback(() => {
      setExpanded((p) => {
        const next = !p;
        if (node.key) onExpandChange?.(node.key, next);
        return next;
      });
    }, [node.key, onExpandChange]);

    if (!editor) return null;

    // min/max-height 必须作用在**内层** .rte-richtext（它有 overflow-y:auto，能真正滚动裁剪），
    // 不能写到 wrapper —— wrapper 是 overflow:visible，给它限高只缩盒子、内容照样溢出可见，
    // 收起态等于没收起。这里挂 CSS 变量到 wrapper，内层用 var() 消费（CSS 变量沿 DOM 继承）。
    // 展开态加 rte-richtext-expanded 类，由 CSS 把内层 max-height 解除（复用既有规则）。
    const wrapperStyle = {
      '--rte-richtext-min-height': `${minHeight}px`,
      '--rte-richtext-max-height': `${maxHeight}px`,
    } as React.CSSProperties;
    const wrapperClass = [
      'rte-richtext-wrapper',
      expandable ? 'rte-richtext-toggleable' : '',
      expanded ? 'rte-richtext-expanded' : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        className={`rte-richtext-container ${expanded ? 'rte-richtext-host-expanded' : ''} ${error ? 'rte-richtext-error' : ''}`}
        data-key={node.key}
      >
        <div className={wrapperClass} style={wrapperStyle}>
          <EditorContent editor={editor} />
          {expandable && <ToggleExpandButton expanded={expanded} onToggle={onToggle} />}
        </div>
      </div>
    );
  },
);
RichtextNode.displayName = 'RichtextNode';
